import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, UserClaims } from '@okta/okta-auth-js';
import { BehaviorSubject, filter, map, firstValueFrom, Subject, Subscription, interval } from 'rxjs';

import { GetAssignedTenantsItem, GetAssignedTenantsResult, UserService } from '../../../service/proxy';
import { AuthorizationResult } from '../../../service/proxy';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private oktaAuthStateService = inject(OktaAuthStateService);
    private userService = inject(UserService);
    private oktaAuth = inject(OKTA_AUTH);
    private router = inject(Router);
    private ngZone = inject(NgZone);

    private permissionsReadySubject = new BehaviorSubject<boolean>(false);
    private tokenExpiryWarningSubject = new Subject<number>();
    private tokenExpiryMonitorSub: Subscription | null = null;
    private authStateMonitorSub: Subscription | null = null;

    private wasAuthenticated: boolean | null = null;
    private isSigningOut = false;
    private tokenManagerHandlersRegistered = false;
    public permissionsReady$ = this.permissionsReadySubject.asObservable();
    public tokenExpiryWarning$ = this.tokenExpiryWarningSubject.asObservable();

    constructor() {
        this.startAuthStateMonitor();
        this.registerTokenManagerExpiryHandlers();
        this.startTokenExpiryMonitor();
    }

    private startAuthStateMonitor(): void {
        if (this.authStateMonitorSub) {
            this.authStateMonitorSub.unsubscribe();
        }

        this.authStateMonitorSub = this.oktaAuthStateService.authState$
            .pipe(filter((authState: AuthState) => !!authState))
            .subscribe((authState: AuthState) => {
                const isAuthenticated = authState.isAuthenticated ?? false;

                if (this.wasAuthenticated === null) {
                    this.wasAuthenticated = isAuthenticated;
                    return;
                }

                // If we were authenticated and now we're not, proactively bounce to home.
                // This catches cases where timers are throttled (background tabs) and the token
                // expires without the interval callback firing.
                if (this.wasAuthenticated && !isAuthenticated) {
                    void this.handleSessionExpired('authState');
                }

                this.wasAuthenticated = isAuthenticated;
            });
    }

    private registerTokenManagerExpiryHandlers(): void {
        if (this.tokenManagerHandlersRegistered) {
            return;
        }

        const tokenManager: any = (this.oktaAuth as any)?.tokenManager;

        if (!tokenManager?.on) {
            return;
        }

        this.tokenManagerHandlersRegistered = true;

        tokenManager.on('expired', () => {
            void this.handleSessionExpired('tokenManager.expired');
        });

        tokenManager.on('error', () => {
            void this.handleSessionExpired('tokenManager.error');
        });
    }

    private async handleSessionExpired(source: string): Promise<void> {
        await this.performSignOut({ reason: `session-expired:${source}`, redirectToHome: true });
    }

    private cleanupLocalAuthState(): void {
        this.permissionsReadySubject.next(false);

        localStorage.removeItem('permissions');
        localStorage.removeItem('tenants');

        if (this.tokenExpiryMonitorSub) {
            this.tokenExpiryMonitorSub.unsubscribe();
            this.tokenExpiryMonitorSub = null;
        }

        this.tokenExpiryWarningSubject.next(-1);
    }

    private navigateHome(): void {
        this.ngZone.run(() => {
            this.router.navigate(['/']);
        });
    }

    private async performSignOut(options: { reason: string; redirectToHome: boolean }): Promise<void> {
        if (this.isSigningOut) {
            return;
        }

        this.isSigningOut = true;

        try {
            await this.oktaAuth.signOut();
        }
        catch (error) {
            // Okta sign-out can fail if the ID token is missing/expired or in some edge cases.
            // Ensure the app still transitions to a logged-out state and redirects.
            try {
                const tokenManager: any = (this.oktaAuth as any)?.tokenManager;
                tokenManager?.clear?.();
            }
            catch {
                // ignore
            }

            try {
                const authStateManager: any = (this.oktaAuth as any)?.authStateManager;
                await authStateManager?.updateAuthState?.();
            }
            catch {
                // ignore
            }
        }
        finally {
            this.cleanupLocalAuthState();

            if (options.redirectToHome) {
                this.navigateHome();
            }

            this.isSigningOut = false;
        }
    }

    private getTokenExpiry(): number | null {
        const idToken = this.oktaAuth.getIdToken();

        if (idToken) {
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            return payload.exp ? payload.exp * 1000 : null;
        }

        const accessToken = this.oktaAuth.getAccessToken();

        if (accessToken) {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            return payload.exp ? payload.exp * 1000 : null;
        }

        return null;
    }

    private startTokenExpiryMonitor() {
        if (this.tokenExpiryMonitorSub) {
            this.tokenExpiryMonitorSub.unsubscribe();
        }

        this.tokenExpiryMonitorSub = interval(1000).subscribe(() => {
            const expiry = this.getTokenExpiry();

            if (!expiry) {
                this.tokenExpiryWarningSubject.next(-1);
                return;
            }

            const now = Date.now();
            const secondsLeft = Math.floor((expiry - now) / 1000);

            if (secondsLeft <= 0) {
                void this.handleSessionExpired('countdown');
                return;
            }

            // Emit warning countdown only in the last 15 minutes.
            if (secondsLeft <= 900) {
                this.tokenExpiryWarningSubject.next(secondsLeft);
            }
            else {
                this.tokenExpiryWarningSubject.next(-1);
            }
        });
    }

    async signIn(): Promise<void> {
        await this.oktaAuth.signInWithRedirect();
        this.startTokenExpiryMonitor();
    }

    async signOut(): Promise<void> {
        await this.performSignOut({ reason: 'user-signout', redirectToHome: true });
    }

    public authenticated$ = this.oktaAuthStateService.authState$.pipe(
        filter((authState: AuthState) => !!authState),
        map((authState: AuthState) => authState.isAuthenticated ?? false)
    );

    async isAuthenticated(): Promise<boolean> {
        return await this.oktaAuth.isAuthenticated();
    }

    async getUserName(): Promise<string | null> {
        // Use display name from database (stored in tenants) instead of Okta claim
        const storedTenants = localStorage.getItem('tenants');

        if (storedTenants) {
            try {
                const tenants = JSON.parse(storedTenants);
                if (tenants?.userDisplayName) {
                    return tenants.userDisplayName;
                }
            } catch {
                // Fall through to Okta claim
            }
        }

        // Fallback to Okta claim if database name not available
        const authState = await firstValueFrom(this.oktaAuthStateService.authState$);
        return authState?.idToken?.claims?.name ?? null;
    }

    async getUserEmail(): Promise<string | null> {
        const authState = await firstValueFrom(this.oktaAuthStateService.authState$);

        return authState?.idToken?.claims?.email ?? null;
    }

    async getTenantName(): Promise<string | null> {
        const storedTenants = localStorage.getItem('tenants');

        if (!storedTenants) {
            return "";
        }

        const tenants: GetAssignedTenantsResult = JSON.parse(storedTenants);

        return tenants.enabledTenant;
    }

    async getTenants(): Promise<GetAssignedTenantsItem[] | null> {
        const storedTenants = localStorage.getItem('tenants');

        if (!storedTenants) {
            return null;
        }

        const tenants: GetAssignedTenantsResult = JSON.parse(storedTenants);

        return tenants.results || null;
    }

    async loadTenantsIntoLocalStorage(): Promise<void> {
        try{
            const storedTenants = localStorage.getItem('tenants');

            if (storedTenants) {
                return;
            }

            const user = await this.oktaAuth.getUser();
            const tenants = await firstValueFrom(this.userService.getAssignedTenants(user.email ?? ''));

            // Check if user has no tenants assigned
            if (!tenants || !tenants.results || tenants.results.length === 0) {
                this.router.navigate(['/not-registered'], { queryParams: { reason: 'no-tenants' } });
                return;
            }

            localStorage.setItem('tenants', JSON.stringify(tenants));
        }
        catch (error) {
            console.error('Error loading tenants into local storage:', error);
            throw error;
        }
    }

    async getUser(): Promise<UserClaims> {
        return await this.oktaAuth.getUser();
    }

    async loadPermissionsIntoLocalStorage(): Promise<void> {
        try{
            const storedPermissions = localStorage.getItem('permissions');

            if (storedPermissions) {
                this.permissionsReadySubject.next(true);
                return;
            }

            const user = await this.oktaAuth.getUser();
            const permissions = await firstValueFrom(this.userService.getUserPermissions(user.email ?? ''));

            localStorage.setItem('permissions', JSON.stringify(permissions));

            this.permissionsReadySubject.next(true);
        }
        catch (error: any) {
            console.error('Error loading permissions into local storage:', error);
            
            // Check if this is a USER_NOT_REGISTERED error from the API
            // SwaggerException has a 'response' property containing the JSON response string
            let isUserNotRegistered = false;
            
            if (error?.status === 403 && error?.response) {
                try {
                    const responseBody = JSON.parse(error.response);
                    isUserNotRegistered = responseBody?.error === 'USER_NOT_REGISTERED';
                } catch {
                    isUserNotRegistered = error.response.includes('USER_NOT_REGISTERED');
                }
            }
            
            if (isUserNotRegistered) {
                this.router.navigate(['/not-registered'], { queryParams: { reason: 'not-registered' } });
                return;
            }
            
            throw error;
        }
    }

    hasPermission(area: string, permission: string): boolean {
        const storedPermissions = localStorage.getItem('permissions');

        if (!storedPermissions) {
            return false;
        }

        const userRoles: AuthorizationResult = JSON.parse(storedPermissions);

        return userRoles.roles.some((role) =>
            role.areas.some((userArea) => {
                const hasPermission = userArea['name'] === area && userArea.permissions.includes(permission);
                return hasPermission;
            })
        );
    }

    hasPermissions(areas: string[], permissions: string[]): boolean {
        const storedPermissions = localStorage.getItem('permissions');

        if (!storedPermissions) {
            return false;
        }

        const userRoles: AuthorizationResult = JSON.parse(storedPermissions);

        return areas.some((area) =>
            userRoles.roles.some((role) =>
                role.areas.some((userArea) =>
                    userArea['name'] === area &&
                    permissions.some((permission) => userArea.permissions.includes(permission))
                )
            )
        );
    }

    hasAnyPermission(areas: string[], permission: string): boolean {
        return areas.some(area => this.hasPermission(area, permission));
    }

    async updateEnabledTenant(tenant: any): Promise<void> {
        const storedTenants = localStorage.getItem('tenants');

        if (!storedTenants) {
            throw new Error('No tenants found in local storage');
        }

        const tenants: GetAssignedTenantsResult = JSON.parse(storedTenants);

        tenants.enabledTenant = tenant.tenantName;
        tenants.enabledTenantId = tenant.tenantId;

        const user = await this.oktaAuth.getUser();

        await firstValueFrom(this.userService.updateEnabledTenant(user.email ?? '', tenant.tenantId));

        localStorage.setItem('tenants', JSON.stringify(tenants));
    }
}
